import React, { useState, useEffect, useRef } from 'react';
import { Smartphone, Wallet, Zap, CheckCircle, AlertCircle, History, ChevronRight, Phone, TrendingUp, Clock } from 'lucide-react';
import useVTUStore from '../../store/vtuStore';
import useWalletStore from '../../store/walletStore';
import useAuthStore from '../../store/authStore';
import TransactionHistory from './components/TransactionHistory';
import { toast } from 'react-hot-toast';

const AirtimePage = () => {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  const { user } = useAuthStore();
  const { wallet, getWallet } = useWalletStore();
  const { detectNetwork, purchaseAirtime, recentRecipients, isLoading, getTransactionSummary, transactionSummary } = useVTUStore();

  const hasLoadedData = useRef(false);

  // Only supported airtime denominations by Alrahuzdata
  const presetAmounts = [100, 200, 500, 1000];

  useEffect(() => {
    if (!hasLoadedData.current) {
      hasLoadedData.current = true;
      getWallet();
      getTransactionSummary();
    }
  }, [getWallet, getTransactionSummary]);

  const handlePhoneChange = async (value) => {
    const cleaned = value.replace(/\D/g, '');
    setPhone(cleaned.slice(0, 11));

    if (cleaned.length === 11) {
      setIsDetecting(true);
      try {
        const result = await detectNetwork(cleaned);
        if (result.success) {
          setSelectedNetwork(result.data.network);
          setStep(2);
        }
      } catch (error) {
        console.error('Network detection failed:', error);
      } finally {
        setIsDetecting(false);
      }
    }
  };

  const handlePresetSelect = (preset) => {
    setAmount(preset.toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const numericAmount = parseFloat(amount);

    if (!phone || phone.length !== 11) {
      toast.error('Please enter a valid 11-digit phone number');
      return;
    }

    if (!numericAmount) {
      toast.error('Please select an amount');
      return;
    }

    // Validate that the amount is supported by Alrahuzdata
    if (!presetAmounts.includes(numericAmount)) {
      toast.error('Please select ₦100, ₦200, ₦500, or ₦1000');
      return;
    }

    if (wallet?.balance < numericAmount) {
      toast.error('Insufficient balance');
      return;
    }

    try {
      await purchaseAirtime({ phone, amount: numericAmount, network: selectedNetwork });
      
      // Reset form
      setPhone('');
      setAmount('');
      setSelectedNetwork(null);
      setStep(1);
      
      // Refresh wallet balance
      getWallet();
    } catch (error) {
      console.error('Airtime purchase failed:', error);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getNetworkColor = (network) => {
    const colors = {
      MTN: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      AIRTEL: 'bg-red-50 text-red-700 border-red-200',
      GLO: 'bg-green-50 text-green-700 border-green-200',
      ETISALAT: 'bg-purple-50 text-purple-700 border-purple-200',
    };
    return colors[network] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const transactionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today - transactionDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return `${diffDays} days ago`;
    }
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5] p-3 sm:p-4 md:p-5">
      <div className="max-w-7xl mx-auto">
        {/* Header - Mobile Optimized */}
        <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 md:p-5 shadow-neomorph mb-3 sm:mb-4 md:mb-5 border border-primary-100/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full" />
                <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-800">Buy Airtime</h1>
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 ml-3 hidden sm:block">Recharge any Nigerian phone number instantly</p>
              <p className="text-[10px] text-gray-500 ml-3 sm:hidden">Recharge instantly</p>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="bg-white rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 shadow-neomorph-inset border border-primary-100/30">
                <p className="text-[9px] sm:text-xs text-gray-500 mb-0.5 hidden sm:block">Wallet Balance</p>
                <div className="flex items-baseline">
                  <span className="text-xs sm:text-sm md:text-base font-bold text-gray-800">
                    {formatAmount(wallet?.balance)}
                  </span>
                  <Wallet className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary-500 ml-1" />
                </div>
              </div>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="bg-white rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-neomorph hover:shadow-neomorph-hover transition-all text-[11px] sm:text-xs font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <History className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5" />
                <span className="hidden sm:inline">{showHistory ? 'Hide History' : 'Show History'}</span>
                <span className="sm:hidden">{showHistory ? 'Hide' : 'History'}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
          {/* Left Column - Purchase Form */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-4 md:space-y-5">
            {/* Recent Recipients - Horizontal scroll on mobile */}
            {recentRecipients && recentRecipients.length > 0 && (
              <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100">
                <div className="flex items-center mb-2 sm:mb-3">
                  <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                  <h3 className="text-xs sm:text-sm font-semibold text-gray-800">Recent Recipients</h3>
                </div>
                <div className="flex flex-nowrap sm:flex-wrap gap-1.5 sm:gap-2 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1">
                  {recentRecipients.map((recipient) => (
                    <button
                      key={recipient}
                      onClick={() => {
                        setPhone(recipient);
                        handlePhoneChange(recipient);
                      }}
                      className="inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-[#E8ECF5] shadow-neomorph-inset hover:shadow-neomorph transition-all text-[11px] sm:text-xs font-medium text-gray-700 whitespace-nowrap flex-shrink-0"
                    >
                      <Phone className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 sm:mr-1.5" />
                      {recipient}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Purchase Form */}
            <div className="bg-white rounded-lg p-4 sm:p-5 shadow-neomorph border border-gray-100">
              {/* Progress Steps - Optimized for mobile */}
              <div className="flex items-center justify-center mb-4 sm:mb-6">
                <div className="flex items-center">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs sm:text-sm font-semibold ${
                    step >= 1 
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30' 
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    1
                  </div>
                  <div className={`w-8 sm:w-12 h-0.5 sm:h-1 mx-0.5 sm:mx-1 rounded-full ${
                    step >= 2 ? 'bg-gradient-to-r from-primary-500 to-primary-600' : 'bg-gray-200'
                  }`} />
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs sm:text-sm font-semibold ${
                    step >= 2 
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30' 
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    2
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                {/* Step 1: Phone Number */}
                <div className={step === 1 ? 'block' : 'hidden'}>
                  <div>
                    <label htmlFor="phone" className="block text-[11px] sm:text-xs font-medium text-gray-600 mb-1 sm:mb-1.5">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-2.5 sm:pl-3 flex items-center pointer-events-none">
                        <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                      </div>
                      <input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        className="block w-full pl-8 sm:pl-9 pr-8 sm:pr-10 py-2 sm:py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                        placeholder="08012345678"
                        maxLength={11}
                        autoFocus
                      />
                      {isDetecting && (
                        <div className="absolute inset-y-0 right-0 pr-2.5 sm:pr-3 flex items-center">
                          <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-primary-500 border-t-transparent"></div>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 sm:mt-1.5 text-[10px] sm:text-xs text-gray-500">Enter 11-digit Nigerian phone number</p>
                  </div>

                  {phone.length === 11 && !isDetecting && !selectedNetwork && (
                    <div className="mt-3 sm:mt-4 bg-yellow-50 rounded-xl p-3 sm:p-4 shadow-neomorph-inset border border-yellow-200/50">
                      <div className="flex items-start">
                        <div className="p-1 sm:p-1.5 bg-yellow-100 rounded-md shadow-neomorph-inset mr-2 sm:mr-3 flex-shrink-0">
                          <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-yellow-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-[11px] sm:text-xs font-medium text-yellow-800 mb-1.5 sm:mb-2">Network Not Detected</h4>
                          <p className="text-[10px] sm:text-xs text-yellow-700 mb-2 sm:mb-3">Please select network manually:</p>
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {['MTN', 'AIRTEL', 'GLO', 'ETISALAT'].map((network) => (
                              <button
                                key={network}
                                type="button"
                                onClick={() => {
                                  setSelectedNetwork(network);
                                  setStep(2);
                                }}
                                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium border ${getNetworkColor(network)} shadow-neomorph-inset`}
                              >
                                {network}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Step 2: Amount Selection */}
                <div className={step === 2 ? 'block space-y-4 sm:space-y-5' : 'hidden'}>
                  {selectedNetwork && (
                    <div className="flex items-center justify-between p-2.5 sm:p-3 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset">
                      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                        <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${getNetworkColor(selectedNetwork).split(' ')[0]} shadow-neomorph-inset`}>
                          <Smartphone className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-[11px] sm:text-xs font-medium text-gray-800">Network Detected</h4>
                          <p className="text-[10px] sm:text-xs text-gray-500 truncate">Sending to {phone}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setStep(1);
                          setSelectedNetwork(null);
                        }}
                        className="text-[10px] sm:text-xs text-primary-600 hover:text-primary-700 font-medium flex-shrink-0 ml-2"
                      >
                        Change
                      </button>
                    </div>
                  )}

                  <div>
                    <label className="block text-[11px] sm:text-xs font-medium text-gray-600 mb-1.5 sm:mb-2">
                      Select Amount
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-2">
                      {presetAmounts.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => handlePresetSelect(preset)}
                          className={`
                            py-2 sm:py-2.5 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all border
                            ${amount === preset.toString()
                              ? 'bg-primary-50 text-primary-700 border-primary-200 shadow-neomorph-inset'
                              : 'bg-white text-gray-700 border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
                            }
                          `}
                        >
                          ₦{preset.toLocaleString()}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-[10px] sm:text-xs text-gray-500">
                      Available denominations: ₦100, ₦200, ₦500, ₦1,000
                    </p>
                  </div>

                  {/* Transaction Summary */}
                  {amount && (
                    <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                      <h4 className="text-[11px] sm:text-xs font-medium text-gray-600 mb-2 sm:mb-3">Transaction Summary</h4>
                      <div className="space-y-1.5 sm:space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] sm:text-xs text-gray-500">Phone Number</span>
                          <span className="text-[10px] sm:text-xs font-medium text-gray-800 truncate ml-2">{phone}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] sm:text-xs text-gray-500">Network</span>
                          <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full ${getNetworkColor(selectedNetwork)}`}>
                            {selectedNetwork}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] sm:text-xs text-gray-500">Amount</span>
                          <span className="text-xs sm:text-sm font-semibold text-gray-800">
                            {formatAmount(parseFloat(amount))}
                          </span>
                        </div>
                        <div className="border-t border-gray-200/50 pt-1.5 sm:pt-2 mt-1.5 sm:mt-2">
                          <div className="flex justify-between items-center">
                            <span className="text-[11px] sm:text-xs font-medium text-gray-700">Total Cost</span>
                            <span className="text-sm sm:text-base font-bold text-primary-600">
                              {formatAmount(parseFloat(amount))}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Wallet Balance Check */}
                  <div className="bg-blue-50 rounded-xl p-3 sm:p-4 shadow-neomorph-inset border border-blue-200/50">
                    <div className="flex items-start">
                      <div className="p-1 sm:p-1.5 bg-blue-100 rounded-md shadow-neomorph-inset mr-2 sm:mr-3 flex-shrink-0">
                        <Wallet className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-[11px] sm:text-xs font-medium text-blue-800 mb-1.5 sm:mb-2">Wallet Balance</h4>
                        <div className="space-y-1">
                          <div className="flex justify-between text-[10px] sm:text-xs">
                            <span className="text-blue-700">Available:</span>
                            <span className="font-semibold text-blue-800">{formatAmount(wallet?.balance)}</span>
                          </div>
                          {amount && (
                            <div className="flex justify-between text-[10px] sm:text-xs">
                              <span className="text-blue-700">After Purchase:</span>
                              <span className={`font-semibold ${
                                wallet?.balance >= parseFloat(amount)
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}>
                                {formatAmount(wallet?.balance - parseFloat(amount))}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2.5 sm:space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setStep(1);
                        setSelectedNetwork(null);
                      }}
                      className="flex-1 bg-white rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs sm:text-sm font-medium text-gray-700 border border-gray-100"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading || !phone || !amount}
                      className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-xs sm:text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-white border-t-transparent mr-1.5 sm:mr-2"></div>
                          <span className="text-xs sm:text-sm">Processing...</span>
                        </>
                      ) : (
                        <>
                          <Zap className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                          Buy Airtime
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column - Stats & Info */}
          <div className="space-y-3 sm:space-y-4 md:space-y-5">
            {/* Stats Card */}
            <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100">
              <div className="flex items-center mb-2 sm:mb-3">
                <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                <h3 className="text-xs sm:text-sm font-semibold text-gray-800">Airtime Stats</h3>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">This Month</p>
                    <p className="text-base sm:text-lg font-bold text-gray-800">
                      {formatAmount(transactionSummary?.airtime?.totalSpent || 0)}
                    </p>
                  </div>
                  <div className="p-1.5 sm:p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-md flex-shrink-0 ml-2">
                    <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                  </div>
                </div>
                
                <div className="border-t border-gray-100 pt-2 sm:pt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 mr-1 sm:mr-1.5" />
                      <p className="text-[10px] sm:text-xs text-gray-500">Transactions</p>
                    </div>
                    <p className="text-sm sm:text-base font-semibold text-gray-800">
                      {transactionSummary?.airtime?.transactionCount || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100">
              <div className="flex items-center mb-2 sm:mb-3">
                <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                <h3 className="text-xs sm:text-sm font-semibold text-gray-800">Quick Actions</h3>
              </div>
              
              <div className="space-y-1.5 sm:space-y-2">
                <button
                  onClick={() => {
                    setPhone(user?.phone || '');
                    handlePhoneChange(user?.phone || '');
                  }}
                  className="w-full flex items-center justify-between p-2.5 sm:p-3 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset hover:shadow-neomorph transition-all"
                >
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="p-1 sm:p-1.5 bg-blue-50 rounded-md shadow-neomorph-inset mr-2 sm:mr-2.5 flex-shrink-0">
                      <Phone className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-blue-600" />
                    </div>
                    <div className="text-left min-w-0 flex-1">
                      <p className="text-[11px] sm:text-xs font-medium text-gray-800 truncate">Recharge My Number</p>
                      <p className="text-[9px] sm:text-[10px] text-gray-500 truncate">{user?.phone}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 flex-shrink-0 ml-2" />
                </button>
                
                <button
                  onClick={() => window.location.href = '/data'}
                  className="w-full flex items-center justify-between p-2.5 sm:p-3 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset hover:shadow-neomorph transition-all"
                >
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="p-1 sm:p-1.5 bg-purple-50 rounded-md shadow-neomorph-inset mr-2 sm:mr-2.5 flex-shrink-0">
                      <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-purple-600" />
                    </div>
                    <div className="text-left min-w-0 flex-1">
                      <p className="text-[11px] sm:text-xs font-medium text-gray-800 truncate">Buy Data Bundle</p>
                      <p className="text-[9px] sm:text-[10px] text-gray-500 truncate">Get internet data plans</p>
                    </div>
                  </div>
                  <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 flex-shrink-0 ml-2" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction History Modal */}
        {showHistory && (
          <TransactionHistory
            serviceType="AIRTIME"
            onClose={() => setShowHistory(false)}
          />
        )}
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default AirtimePage;